/** Keys on `data` that should not trigger “wijziging” logs (navigation / audit meta). */
const IGNORE_DATA_KEYS = new Set(['flowStep', 'flowStepHistory', 'intakeVersionHistory']);

/** Keys we already describe with explicit Dutch labels (avoid duplicate + generic noise). */
const EXPLICIT_DATA_KEYS = new Set([
  'planningDate',
  'closingDate',
  'startDate',
  'location',
  'jobCode',
  'workplaceType',
  'category',
  'hoursPerWeek',
  'contractType',
  'contractDuration',
  'placementTypes',
  'experience',
  'education',
  'benefits',
  'suggestions',
  'organizationText',
  'urgencyLevel',
  'campaignChoice',
  'salary',
  'vacancyText',
  'finalVacancyContent',
  'publishedToSite',
  'lifecycleStatus',
  'quick_note',
  'booleanString',
  'clientName',
  'opdrachtgeverLogoUrl',
  'werfplaatsTeamConfig',
  'werfplaatsConfig',
  'hiringPlan',
  'premiumJobboards',
  'marketResearch',
  'persona',
  'personaConfig',
  'structuredTemplates',
  'builtVacancySections',
  'tags',
  'activePlatforms',
  'marketingType',
  'teamRoles',
  'externalReviewers',
  'askWerfplaats',
  'contactPersonId',
  'claimStatus',
  'tone',
  'isClosed',
  'publishDate',
  'linkedinSearchString',
  'metaAdsCopy',
  'marketingStrategy',
  'marketingConfig',
  'verificationReport',
  'massMarketingAdvice',
  'abTestVariants',
  'abTestStatus',
  'toneAndSeo',
  'marketResearchInsights',
  'personas',
]);

const stripIgnored = (data?: Record<string, unknown>): Record<string, unknown> => {
  const o = { ...(data || {}) };
  IGNORE_DATA_KEYS.forEach((k) => delete o[k]);
  return o;
};

const eq = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

const normText = (s: unknown) => String(s ?? '').replace(/\s+/g, ' ').trim();

export type VacancySnapshotForDiff = {
  title?: string;
  description?: string;
  data?: Record<string, unknown>;
  opdrachtgever_id?: string | null;
  eindklant_id?: string | null;
  shared_with_users?: unknown;
  shared_with_clients?: unknown;
  /** DB column `vacancies.status` (e.g. Concept, Open) */
  status?: string | null;
};

/**
 * Human-readable Dutch lines describing what changed between two vacancy snapshots.
 * Used for activity / audit (not full field-level diff UI).
 */
export function describeVacancySnapshotChanges(
  prev: VacancySnapshotForDiff,
  next: VacancySnapshotForDiff,
  maxLines = 24
): string[] {
  const out: string[] = [];

  if (!eq(prev.title, next.title)) out.push('Functietitel aangepast');
  if (normText(prev.description) !== normText(next.description)) out.push('Vacaturetekst / beschrijving (HTML) aangepast');
  if (!eq(prev.status, next.status)) out.push('Vacaturestatus (kolom) gewijzigd');

  const pd = stripIgnored(prev.data);
  const nd = stripIgnored(next.data);

  const track = (key: string, label: string) => {
    if (!eq(pd[key], nd[key])) out.push(`${label} aangepast`);
  };

  track('planningDate', 'Startdatum werving');
  track('closingDate', 'Sluitingsdatum');
  track('startDate', 'Ingangsdatum');
  track('location', 'Locatie / standplaats');
  track('jobCode', 'Jobcode');
  track('workplaceType', 'Werkvorm');
  track('category', 'Dienstverband');
  track('hoursPerWeek', 'Uren per week');
  track('contractType', 'Contracttype');
  track('contractDuration', 'Contractduur');
  track('placementTypes', 'Plaatsingsvorm');
  track('experience', 'Ervaring');
  track('education', 'Opleidingsniveau');
  track('benefits', 'Arbeidsvoorwaarden');
  track('suggestions', 'Functie-eisen / vereisten');
  track('organizationText', 'Organisatietekst');
  track('urgencyLevel', 'Urgentie');
  track('campaignChoice', 'Campagnekeuze');
  track('salary', 'Salaris');
  track('vacancyText', 'Brontekst intake');
  track('finalVacancyContent', 'Definitieve vacaturetekst');
  track('publishedToSite', 'Publicatie op werken-bij');
  track('lifecycleStatus', 'Levenscyclus / status');
  track('quick_note', 'Snelle notitie');
  track('booleanString', 'Boolean / zoekstring');
  track('clientName', 'Weergavenaam klant');
  track('opdrachtgeverLogoUrl', 'Logo opdrachtgever');

  if (!eq(prev.opdrachtgever_id, next.opdrachtgever_id)) out.push('Opdrachtgever (klant) gewijzigd');
  if (!eq(prev.eindklant_id, next.eindklant_id)) out.push('Werkplek (eindklant) gewijzigd');
  if (!eq(prev.shared_with_users, next.shared_with_users)) out.push('Delen met collega’s aangepast');
  if (!eq(prev.shared_with_clients, next.shared_with_clients)) out.push('Delen met klanten aangepast');

  if (!eq(pd.werfplaatsTeamConfig, nd.werfplaatsTeamConfig)) out.push('Werfplaats-teamconfig aangepast');
  if (!eq(pd.werfplaatsConfig, nd.werfplaatsConfig)) out.push('Team / campagne (Werfplaats) aangepast');
  if (!eq(pd.hiringPlan, nd.hiringPlan)) out.push('Hiring plan aangepast');
  if (!eq(pd.premiumJobboards, nd.premiumJobboards)) out.push('Premium job boards aangepast');
  if (!eq(pd.marketResearch, nd.marketResearch)) out.push('Marktonderzoek aangepast');
  if (!eq(pd.persona, nd.persona)) out.push('Personatekst aangepast');
  if (!eq(pd.personaConfig, nd.personaConfig)) out.push('Persona-configuratie aangepast');
  if (!eq(pd.structuredTemplates, nd.structuredTemplates)) out.push('Vacature-secties / sjablonen aangepast');
  if (!eq(pd.builtVacancySections, nd.builtVacancySections)) out.push('Vacatureblokken (builder) aangepast');
  if (!eq(pd.tags, nd.tags)) out.push('Tags aangepast');
  if (!eq(pd.activePlatforms, nd.activePlatforms)) out.push('Actieve kanalen aangepast');
  if (!eq(pd.marketingType, nd.marketingType)) out.push('Marketingtype aangepast');

  track('teamRoles', 'Teamrollen per collega');
  track('externalReviewers', 'Externe reviewers');
  track('askWerfplaats', 'Werfplaats-hulpverzoek');
  track('contactPersonId', 'Hoofdcontactpersoon (publicatie)');
  track('claimStatus', 'Claim / leadstatus');
  track('tone', 'Toon van de vacature');
  track('isClosed', 'Vacature gesloten');
  track('publishDate', 'Publicatiedatum');
  track('linkedinSearchString', 'LinkedIn-zoekstring');
  track('metaAdsCopy', 'Meta-ads tekst');
  track('marketingStrategy', 'Marketingstrategie');
  track('marketingConfig', 'Marketingconfiguratie');
  track('verificationReport', 'Verificatierapport');
  track('massMarketingAdvice', 'Mass marketing advies');
  track('abTestVariants', 'A/B-test varianten');
  track('abTestStatus', 'A/B-test status');
  track('toneAndSeo', 'Toon & SEO');
  track('marketResearchInsights', 'Marktinzichten');
  track('personas', 'Persona’s (lijst)');

  const allKeys = new Set([...Object.keys(pd), ...Object.keys(nd)]);
  const sortedExtra = [...allKeys].filter((k) => !IGNORE_DATA_KEYS.has(k) && !EXPLICIT_DATA_KEYS.has(k)).sort();
  for (const key of sortedExtra) {
    if (out.length >= maxLines) break;
    if (!eq(pd[key], nd[key])) out.push(`Vacature-data (“${key}”) gewijzigd`);
  }

  return out.slice(0, maxLines);
}
